import React from "react";
import { FiCpu } from "react-icons/fi";
import { GiFuelTank } from "react-icons/gi";
import { SectionHeader } from "../../../components/Shared";
import { MiniBar } from "./facilityHelpers";

const ElectricalEnergyPanel = ({ d }) => {
  const e = d.electrical;
  const dg = d.dg;
  const loadPct = ((e.totalLoad / e.maxLoad) * 100).toFixed(0);
  const maxWeekly = Math.max(...d.energy.weekly.map(w => w.v));
  const maxHourly = Math.max(...d.energy.hourly.map(h => h.v));
  const maxDgFuel = Math.max(...dg.weeklyHistory.map(w => w.fuelUsed), 1);
  const maxDgHours = Math.max(...dg.weeklyHistory.map(w => w.runHours), 1);
  const totalWeekFuel = dg.weeklyHistory.reduce((s, w) => s + w.fuelUsed, 0);
  const totalWeekHours = dg.weeklyHistory.reduce((s, w) => s + w.runHours, 0).toFixed(1);

  return (
    <div>
      {/* DG Status */}
      <SectionHeader title="Diesel Generator (DG) Status" />
      <div className="fm-dg-section">
        {/* DG Info Card */}
        <div className="fm-dg-info-card">
          <div className="fm-dg-info-header">
            <GiFuelTank size={28} color="#dc2626" />
            <div>
              <div className="fm-dg-info-model">{dg.model}</div>
              <div className="fm-dg-info-cap">{dg.capacity}</div>
            </div>
            <span className={`fm-dg-status-badge dg-status-${dg.status.toLowerCase()}`}>{dg.status}</span>
          </div>

          {/* Fuel gauge */}
          <div className="fm-dg-fuel-gauge">
            <div className="fm-dg-fuel-gauge-labels">
              <span>Fuel Level</span>
              <span style={{ color: dg.fuelLevel < 30 ? "#ef4444" : dg.fuelLevel < 50 ? "#f59e0b" : "#10b981", fontWeight: 700 }}>
                {dg.fuelLevel}% &nbsp;({Math.floor(dg.fuelLevel / 100 * dg.fuelCapacity)} / {dg.fuelCapacity} L)
              </span>
            </div>
            <MiniBar value={dg.fuelLevel} max={100} height={12}
              color={dg.fuelLevel < 30 ? "#ef4444" : dg.fuelLevel < 50 ? "#f59e0b" : "#10b981"} />
          </div>

          {/* Metrics grid */}
          <div className="fm-dg-metrics">
            {[
              { label: "Output Voltage", val: `${dg.voltage} V`,           color: "#f59e0b" },
              { label: "Frequency",      val: `${dg.frequency} Hz`,         color: "#8b5cf6" },
              { label: "Load",           val: dg.status === "Standby" ? "— (Standby)" : `${dg.loadPercent}%`, color: "#3b82f6" },
              { label: "Coolant Temp",   val: `${dg.coolingTemp} °C`,       color: dg.coolingTemp > 95 ? "#ef4444" : "#10b981" },
              { label: "Oil Pressure",   val: `${dg.oilPressure} psi`,      color: dg.oilPressure < 30 ? "#ef4444" : "#10b981" },
              { label: "Battery",        val: `${dg.batteryVoltage} V`,     color: dg.batteryVoltage < 23 ? "#ef4444" : "#10b981" },
              { label: "Lifetime Hours", val: `${dg.totalRunHoursLifetime.toLocaleString()} hrs`, color: "#6b7280" },
              { label: "Last Service",   val: dg.lastServiceDate,           color: "#374151" },
              { label: "Next Service",   val: dg.nextServiceDate,           color: "#667eea" },
            ].map((m, i) => (
              <div key={i} className="fm-dg-metric-item">
                <div className="fm-dg-metric-val" style={{ color: m.color }}>{m.val}</div>
                <div className="fm-dg-metric-lbl">{m.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* DG Fuel History Chart */}
        <div className="fm-dg-history-card">
          <div className="fm-dg-history-title">DG Fuel Consumption – Last 7 Days</div>
          <div className="fm-dg-history-summary">
            <span>Total Fuel: <strong>{totalWeekFuel} L</strong></span>
            <span>Total Run: <strong>{totalWeekHours} hrs</strong></span>
          </div>

          {/* Bar chart */}
          <div className="fm-dg-bar-chart">
            {dg.weeklyHistory.map((w, i) => (
              <div key={i} className="fm-dg-bar-col">
                <div className="fm-dg-bar-values">
                  {w.fuelUsed > 0 && <span className="fm-dg-bar-tip">{w.fuelUsed}L</span>}
                </div>
                <div className="fm-dg-bars">
                  <div className="fm-dg-bar-wrap">
                    <div className="fm-dg-bar-fill fm-dg-bar-fuel"
                      style={{ height: `${(w.fuelUsed / maxDgFuel) * 100}%` }} />
                  </div>
                  <div className="fm-dg-bar-wrap">
                    <div className="fm-dg-bar-fill fm-dg-bar-hours"
                      style={{ height: `${(w.runHours / maxDgHours) * 100}%` }} />
                  </div>
                </div>
                <div className="fm-dg-bar-day">{w.day}</div>
                <div className="fm-dg-bar-hours-lbl">{w.runHours > 0 ? `${w.runHours}h` : "—"}</div>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="fm-dg-legend">
            <span className="fm-dg-legend-item"><span className="fm-dg-legend-dot fuel-dot" />Fuel Used (L)</span>
            <span className="fm-dg-legend-item"><span className="fm-dg-legend-dot hours-dot" />Run Hours</span>
          </div>

          {/* History table */}
          <div className="fm-dg-history-table-wrap">
            <table className="fm-dg-history-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Fuel Used (L)</th>
                  <th>Run Hours</th>
                  <th>Start Count</th>
                  <th>Avg Load (%)</th>
                </tr>
              </thead>
              <tbody>
                {dg.weeklyHistory.map((w, i) => (
                  <tr key={i} className={i % 2 === 0 ? "fm-row-even" : "fm-row-odd"}>
                    <td><strong>{w.day}</strong></td>
                    <td style={{ color: w.fuelUsed > 70 ? "#ef4444" : w.fuelUsed > 0 ? "#374151" : "#94a3b8" }}>
                      {w.fuelUsed > 0 ? `${w.fuelUsed} L` : "—"}
                    </td>
                    <td style={{ color: w.runHours > 0 ? "#3b82f6" : "#94a3b8" }}>
                      {w.runHours > 0 ? `${w.runHours} hrs` : "—"}
                    </td>
                    <td style={{ color: w.startCount > 0 ? "#374151" : "#94a3b8" }}>
                      {w.startCount > 0 ? w.startCount : "—"}
                    </td>
                    <td style={{ color: w.avgLoad > 0 ? "#10b981" : "#94a3b8" }}>
                      {w.avgLoad > 0 ? `${w.avgLoad}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Electrical */}
      <SectionHeader title="Electrical Monitoring" />
      <div className="fm-electrical-grid">
        {/* Main readings */}
        <div className="fm-elec-card fm-elec-main">
          <div className="fm-elec-grid2">
            {[
              { label: "Voltage",       val: `${e.voltage} V`,              color: "#f59e0b" },
              { label: "Current",       val: `${e.current} A`,              color: "#3b82f6" },
              { label: "Power Factor",  val: e.powerFactor,                 color: "#10b981" },
              { label: "Frequency",     val: `${e.frequency} Hz`,           color: "#8b5cf6" },
              { label: "Phase Imbal.",  val: `${e.phaseImbalance}%`,        color: e.phaseImbalance > 2 ? "#ef4444" : "#10b981" },
              { label: "DG Status",     val: e.dgStatus,                    color: "#6b7280" },
              { label: "UPS Status",    val: e.upsStatus,                   color: "#10b981" },
            ].map((item, i) => (
              <div key={i} className="fm-elec-metric">
                <div className="fm-elec-val" style={{ color: item.color }}>{item.val}</div>
                <div className="fm-elec-lbl">{item.label}</div>
              </div>
            ))}
          </div>
          <div className="fm-elec-load">
            <div className="fm-elec-load-lbl">Total Load: {e.totalLoad} kW / {e.maxLoad} kW ({loadPct}%)</div>
            <MiniBar value={e.totalLoad} max={e.maxLoad} color={loadPct > 80 ? "#ef4444" : loadPct > 60 ? "#f59e0b" : "#10b981"} height={8} />
          </div>
        </div>

        {/* Floor-wise energy */}
        <div className="fm-elec-card">
          <div className="fm-elec-floor-title">Floor-wise Consumption (kWh today)</div>
          {e.floors.map((fl, i) => (
            <div key={i} className="fm-floor-row">
              <div className="fm-floor-label">{fl.floor}</div>
              <div className="fm-floor-bar-wrap">
                <div className="fm-floor-bar-bg">
                  <div className="fm-floor-bar-fill" style={{
                    width: `${Math.min((fl.consumption / 65) * 100, 100)}%`,
                    background: fl.consumption > fl.maxNorm ? "linear-gradient(90deg,#f87171,#ef4444)" : "linear-gradient(90deg,#34d399,#10b981)"
                  }} />
                </div>
                <span className="fm-floor-val">{fl.consumption} kWh</span>
              </div>
              {fl.consumption > fl.maxNorm && <span className="fm-floor-over">HIGH</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Energy Analytics */}
      <SectionHeader title="Energy Analytics" />
      <div className="fm-energy-stats">
        <div className="fm-energy-stat"><span className="fm-energy-val">{d.summary.energyToday} kWh</span><span className="fm-energy-lbl">Today</span></div>
        <div className="fm-energy-stat"><span className="fm-energy-val">{d.energy.carbonFootprintKg} kg</span><span className="fm-energy-lbl">CO₂ Today</span></div>
        <div className="fm-energy-stat"><span className="fm-energy-val">{d.energy.peakLoadTime}</span><span className="fm-energy-lbl">Peak Load Time</span></div>
      </div>

      <div className="fm-charts-row">
        {/* Hourly chart */}
        <div className="fm-chart-card">
          <div className="fm-chart-title">Hourly Consumption (kWh)</div>
          <div className="fm-bar-chart">
            {d.energy.hourly.map((h, i) => (
              <div key={i} className="fm-bar-col">
                <div className="fm-bar-wrap">
                  <div className="fm-bar-fill" style={{ height: `${(h.v / maxHourly) * 100}%`, background: h.v === maxHourly ? "#ef4444" : "#667eea" }} />
                </div>
                <div className="fm-bar-label">{h.h}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly chart */}
        <div className="fm-chart-card">
          <div className="fm-chart-title">Weekly Consumption (kWh)</div>
          <div className="fm-bar-chart">
            {d.energy.weekly.map((w, i) => (
              <div key={i} className="fm-bar-col">
                <div className="fm-bar-wrap">
                  <div className="fm-bar-fill" style={{ height: `${(w.v / maxWeekly) * 100}%`, background: w.v === maxWeekly ? "#ef4444" : "#10b981" }} />
                </div>
                <div className="fm-bar-label">{w.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI tips */}
      <div className="fm-ai-tips-box">
        <div className="fm-ai-tips-title"><FiCpu size={16}/> AI Energy Recommendations</div>
        {d.energy.aiTips.map((tip, i) => (
          <div key={i} className="fm-ai-tip-row"><span className="fm-ai-num">{i + 1}</span>{tip}</div>
        ))}
      </div>
    </div>
  );
};

export default ElectricalEnergyPanel;
